import { api } from '../../core/network/api';

/**
 * Typed wrappers for the `/api/admin/*` surface the in-app admin screen uses.
 *
 * Authentication is the ordinary session cookie. Core's AdminAccessGuard also
 * accepts an `x-api-key` header (that is how MAD connects), but a signed-in
 * user whose CURRENT database role is ADMIN is the path that applies here —
 * which is why the screen can be reached with no extra credential and why
 * hiding the tab is a courtesy, not the access control. Every route below is
 * re-checked server-side on each call.
 *
 * The shapes are deliberately loose where the admin API returns free-form
 * records (settings, meta). Pinning them here would mean a second, drifting
 * copy of a contract that core already owns and MAD already consumes.
 */

export interface AdminStats {
  [key: string]: unknown;
}

export interface AdminUser {
  id: string;
  email: string;
  username: string | null;
  role: string;
  banned: boolean;
  emailVerified: boolean;
  kycStatus: string;
  level?: number;
  createdAt: string;
}

/** Admin list endpoints return `{items,total,page,pageSize}` since the merge. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page?: number;
  pageSize?: number;
}

export interface AdminInvoice {
  id: string;
  number: string;
  type: string;
  status: string;
  totalCents: number;
  currency: string;
  description: string;
  createdAt: string;
}

export interface AdminApiKey {
  id: string;
  label: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt?: string | null;
}

export interface ServerStatus {
  [key: string]: unknown;
}

/** `GET /admin/users/:id` — every column except the password hash. */
export interface AdminUserDetail extends AdminUser {
  kycFullName: string | null;
  kycDateOfBirth: string | null;
  kycAddress: string | null;
  kycCountry: string | null;
  kycDocumentUrl: string | null;
  kycSubmittedAt: string | null;
  kycReviewedAt: string | null;
  kycRejectReason: string | null;
  softBalance?: number;
  premiumBalance?: number;
  [key: string]: unknown;
}

export interface AdminColony {
  id: string;
  name: string;
  status: string;
  world: { id: string; name: string } | null;
}

export interface AdminGrantBody {
  soft?: number;
  premium?: number;
  colonyId?: string;
  resources?: { type: string; amount: number }[];
  reason?: string;
}

export interface AdminGrantResult {
  soft?: number;
  premium?: number;
  /** What the storage cap actually allowed, which can be less than requested. */
  resources: Record<string, number>;
}

/** The five game resources, in the order the grant form lists them. */
export const RESOURCE_TYPES = ['REGOLITH', 'WATER_ICE', 'HE3', 'COMPOSITE', 'CRIMSON_DUST'] as const;
export const KYC_STATUSES = ['NONE', 'PENDING', 'APPROVED', 'REJECTED'] as const;
export const USER_ROLES = ['PLAYER', 'ADMIN', 'KYC_ADMIN'] as const;

/**
 * The slice of `GET /admin/payments/config` the Overview switches care about.
 *
 * `withdrawalsEnabled` comes back as the EFFECTIVE value — core ANDs the stored
 * flag with "not preview mode" and "payments adopted", so it can read false
 * while `feature.withdrawals` is stored true. `kycEnabled` is returned raw.
 * The switches show what the server reports, which is what actually governs
 * behaviour; the stored value is in `GET /admin/settings` if they disagree.
 */
export interface AdminPaymentConfig {
  withdrawalsEnabled: boolean;
  kycEnabled: boolean;
  /** False until a game calls `paymentsService.useLedger()`. */
  adopted?: boolean;
  [key: string]: unknown;
}

export interface AdminDepositMethod {
  id: string;
  label: string;
  icon: string;
  provider: string;
  configured: boolean;
  enabled: boolean;
  active: boolean;
  currencies: string[];
  currencyOptions: string[];
  billing: 'required' | 'optional';
  minBilling: 'required' | 'optional';
}

export interface AdminPayoutMethod {
  id: string;
  label: string;
  icon: string;
  provider: string;
  receiver: 'cardNumber' | 'iban';
  configured: boolean;
  enabled: boolean;
  active: boolean;
  currencies: string[];
  billing: 'required';
  pollable: boolean;
}

export interface AdminPaymentLimits {
  depositMinCents: number;
  depositMaxCents: number;
  depositFeeBps: number;
  depositFeePct: number;
  depositKycThresholdCents: number;
  requireVerifiedEmail: boolean;
  withdrawalMinCents: number;
  withdrawalMaxCents: number;
  withdrawalFeeBps: number;
  withdrawalFeePct: number;
  capToDeposits: boolean;
}

export interface AdminFullPaymentConfig {
  adopted: boolean;
  currencies: string[];
  defaultCurrency: string;
  withdrawalsEnabled: boolean;
  kycEnabled: boolean;
  deposit: AdminDepositMethod[];
  payout: AdminPayoutMethod[];
  limits: AdminPaymentLimits;
}

export const FEATURE_WITHDRAWALS = 'feature.withdrawals';
export const FEATURE_KYC = 'feature.kyc';

/**
 * Every `game_settings` row, as stored strings.
 *
 * The feature switches read from HERE rather than from the payments config,
 * because that endpoint masks both flags behind the effective state:
 * `withdrawalsEnabled: !preview && withdrawalsEnabled`, where `preview` is
 * `isPreview() || !adopted`. This game has not adopted core payments, so the
 * config endpoint reports withdrawals as false no matter what is stored — a
 * switch bound to it flips, saves correctly, and then snaps back, which is
 * indistinguishable from being broken.
 */
export const adminGetSettings = () => api.get<Record<string, string>>('/admin/settings');

export const adminStats = () => api.get<AdminStats>('/admin/stats/overview');
export const adminServerStatus = () => api.get<ServerStatus>('/admin/server-status');

export const adminGetPaymentConfig = () => api.get<AdminPaymentConfig>('/admin/payments/config');
export const adminGetFullPaymentConfig = () => api.get<AdminFullPaymentConfig>('/admin/payments/config');
export const adminPatchPaymentConfig = (body: Record<string, unknown>) =>
  api.patch<AdminFullPaymentConfig>('/admin/payments/config', body);

export const adminListUsers = (params: { search?: string; page?: number } = {}) => {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.page) q.set('page', String(params.page));
  const suffix = q.toString() ? `?${q}` : '';
  return api.get<Paginated<AdminUser> | AdminUser[]>(`/admin/users${suffix}`);
};
export const adminBanUser = (id: string) => api.post<unknown>(`/admin/users/${id}/ban`, {});
export const adminUnbanUser = (id: string) => api.post<unknown>(`/admin/users/${id}/unban`, {});
export const adminVerifyEmail = (id: string) => api.post<unknown>(`/admin/users/${id}/verify-email`, {});
export const adminVerifyKyc = (id: string) => api.post<unknown>(`/admin/users/${id}/verify-kyc`, {});

/** Full row minus the password hash — the only source of the submitted KYC detail. */
export const adminGetUser = (id: string) => api.get<AdminUserDetail>(`/admin/users/${id}`);

export const adminCreateUser = (body: {
  email: string;
  username?: string;
  password: string;
  role?: string;
  emailVerified?: boolean;
  kycStatus?: string;
}) => api.post<{ id: string; email: string; username: string | null }>('/admin/users/create', body);

/**
 * Core owns password setting, and it does more than write a hash: it also
 * wipes every refresh token and any pending reset. The game-side player PATCH
 * deliberately leaves passwords alone so there is only one path that
 * invalidates sessions.
 */
export const adminSetPassword = (id: string, password: string) =>
  api.post<unknown>(`/admin/users/${id}/password`, { password });

// ─── Game-owned routes (backend/src/game/admin) ──────────────────────────
// These exist because core has no equivalent; see that controller's comments.

/** Like `adminListUsers`, but `search` also matches an exact player id. */
export const adminListPlayers = (params: { search?: string; page?: number; pageSize?: number } = {}) => {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('per_page', String(params.pageSize));
  const suffix = q.toString() ? `?${q}` : '';
  return api.get<Paginated<AdminUser> | AdminUser[]>(`/admin/players${suffix}`);
};

export const adminUpdatePlayer = (
  id: string,
  body: { email?: string; username?: string; role?: string; emailVerified?: boolean },
) => api.patch<unknown>(`/admin/players/${id}`, body);

/** Any status from any status — core's approve/reject are PENDING-only. */
export const adminSetKycStatus = (id: string, status: string, reason?: string) =>
  api.patch<unknown>(`/admin/players/${id}/kyc-status`, { status, ...(reason ? { reason } : {}) });

export const adminPlayerColonies = (id: string) =>
  api.get<AdminColony[]>(`/admin/players/${id}/colonies`);

export const adminGrant = (id: string, body: AdminGrantBody) =>
  api.post<AdminGrantResult>(`/admin/players/${id}/grant`, body);

export const adminListInvoices = () => api.get<Paginated<AdminInvoice> | AdminInvoice[]>('/admin/invoices');

/**
 * `amountCents` is the NET figure. The server computes
 * `vatCents = round(amountCents * vatRate / 100)` and `totalCents` itself —
 * sending the gross here would tax an already-taxed amount.
 */
export const adminGenerateInvoice = (body: {
  userId: string;
  type: 'IN' | 'OUT';
  amountCents: number;
  vatRate: number;
  currency?: string;
  description: string;
  status?: 'PAID' | 'PENDING';
  paymentMethod?: string;
  transactionId?: string;
  /** Joined with a space into one recipient name by core's controller. */
  recipientName?: string;
  recipientSurname?: string;
  recipientCompany?: string;
  recipientCode?: string;
}) => api.post<AdminInvoice>('/admin/invoices/generate', body);

export const adminGetCompany = () => api.get<Record<string, string>>('/admin/settings/company');
export const adminSaveCompany = (body: Record<string, string>) =>
  api.post<unknown>('/admin/settings/company', body);

export const adminGetMeta = () => api.get<Record<string, unknown>>('/admin/meta');

/**
 * Bulk meta update.
 *
 * The body is `{ settings: { ... } }` with BARE keys — not a flat object, and
 * not the `meta.`-prefixed names the values are stored under. Both of those
 * other shapes are rejected with a 400 ("property title should not exist",
 * "settings must be an object"), so the wrapper is load-bearing.
 */
export const adminSaveMeta = (settings: Record<string, string>) =>
  api.post<unknown>('/admin/meta', { settings });

// ─── CMS ──────────────────────────────────────────────────────────────────
// Legal, FAQ and news all share one 6-verb shape in core's cms.controller.

export interface CmsLegal { id: string; slug: string; title: string; content: string; active: boolean; updatedAt?: string }
export interface CmsFaq { id: string; question: string; answer: string; sortOrder: number; active: boolean }
export interface CmsNews {
  id: string; slug: string; title: string; content?: string; summary?: string | null;
  tag?: string | null; published: boolean; publishedAt?: string | null; imageUrl?: string | null; createdAt?: string;
}

export const adminListLegal = () => api.get<Paginated<CmsLegal> | CmsLegal[]>('/admin/legal');
export const adminGetLegal = (id: string) => api.get<CmsLegal>(`/admin/legal/${id}`);
export const adminCreateLegal = (body: Partial<CmsLegal>) => api.post<CmsLegal>('/admin/legal', body);
export const adminUpdateLegal = (id: string, body: Partial<CmsLegal>) => api.patch<CmsLegal>(`/admin/legal/${id}`, body);
export const adminDeleteLegal = (id: string) => api.del<unknown>(`/admin/legal/${id}`);

export const adminListFaq = () => api.get<Paginated<CmsFaq> | CmsFaq[]>('/admin/faq');
export const adminCreateFaq = (body: Partial<CmsFaq>) => api.post<CmsFaq>('/admin/faq', body);
export const adminUpdateFaq = (id: string, body: Partial<CmsFaq>) => api.patch<CmsFaq>(`/admin/faq/${id}`, body);
export const adminDeleteFaq = (id: string) => api.del<unknown>(`/admin/faq/${id}`);

export const adminListNews = () => api.get<Paginated<CmsNews> | CmsNews[]>('/admin/news');
export const adminGetNews = (id: string) => api.get<CmsNews>(`/admin/news/${id}`);
export const adminCreateNews = (body: Partial<CmsNews>) => api.post<CmsNews>('/admin/news', body);
export const adminUpdateNews = (id: string, body: Partial<CmsNews>) => api.patch<CmsNews>(`/admin/news/${id}`, body);
export const adminDeleteNews = (id: string) => api.del<unknown>(`/admin/news/${id}`);

export interface CmsSocial { id: string; platform: string; url: string; icon: string; sortOrder: number; active: boolean }

export const adminListSocial = () => api.get<Paginated<CmsSocial> | CmsSocial[]>('/admin/social-links');
export const adminCreateSocial = (body: Partial<CmsSocial>) => api.post<CmsSocial>('/admin/social-links', body);
export const adminUpdateSocial = (id: string, body: Partial<CmsSocial>) => api.patch<CmsSocial>(`/admin/social-links/${id}`, body);
export const adminDeleteSocial = (id: string) => api.del<unknown>(`/admin/social-links/${id}`);

export const adminListApiKeys = () => api.get<Paginated<AdminApiKey> | AdminApiKey[]>('/admin/api-keys');
export const adminCreateApiKey = (label: string) => api.post<{ key?: string }>('/admin/api-keys', { label });
export const adminDeleteApiKey = (id: string) => api.del<unknown>(`/admin/api-keys/${id}`);

/**
 * Unwraps a list response that may be either a bare array (the pre-merge
 * shape, still returned by some routes) or the paginated envelope the admin
 * contract now specifies. Written once so no pane has to guess.
 */
export function listItems<T>(res: Paginated<T> | T[] | null | undefined): T[] {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.items)) return res.items;
  return [];
}
